import { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Plus, Trash, X, UserCircle } from "@phosphor-icons/react";
import { useCountry } from "../context/CountryContext";

export default function Drivers() {
  const { filterParam } = useCountry();
  const [rows, setRows] = useState([]);
  const [prod, setProd] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", notes: "" });
  const [err, setErr] = useState("");

  const load = async () => {
    const params = filterParam ? { country: filterParam } : {};
    const [d, p] = await Promise.all([
      api.get("/drivers"),
      api.get("/activities/productivity", { params }).catch(() => ({ data: null })),
    ]);
    setRows(d.data);
    setProd(p.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filterParam]);

  const tripsByDriver = useMemo(() => {
    const m = {};
    for (const r of prod?.by_driver || []) if (r.driver_name) m[r.driver_name] = r.trips;
    return m;
  }, [prod]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    try {
      await api.post("/drivers", form);
      setOpen(false);
      setForm({ name: "", phone: "", notes: "" });
      await load();
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this driver?")) return;
    await api.delete(`/drivers/${id}`);
    await load();
  };

  return (
    <div data-testid="drivers-page">
      <PageHeader
        subtitle="Personnel registry"
        title="Drivers"
        action={
          <button onClick={() => setOpen(true)} data-testid="new-driver-button" className="bg-ps-blue text-white px-4 py-2.5 rounded-md font-semibold flex items-center gap-2 hover:bg-ps-deep transition-colors shadow-sm">
            <Plus size={16} weight="bold" /> Register driver
          </button>
        }
      />

      <div className="p-8">
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="px-6 py-3 border-b border-slate-200 flex items-center justify-between">
            <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500">
              {rows.length} drivers on file
            </div>
            {prod && prod.total_trips > 0 && (
              <div className="text-[10px] uppercase tracking-[0.25em] text-ps-blue font-semibold" data-testid="drivers-prod-summary">
                <span className="text-slate-500">trips this month:</span> <span className="font-mono-data text-ps-deep tracking-normal">{prod.total_trips}</span>
              </div>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-slate-200" data-testid="drivers-grid">
            {rows.map((d) => {
              const trips = tripsByDriver[d.name];
              return (
                <div key={d.id} className="bg-white p-4 flex items-center justify-between hover:bg-slate-50" data-testid={`driver-card-${d.id}`}>
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-10 h-10 bg-slate-100 text-slate-900 grid place-items-center border border-slate-300 shrink-0"><UserCircle size={18} weight="duotone" /></div>
                    <div className="min-w-0">
                      <div className="font-semibold tracking-wide truncate">{d.name}</div>
                      <div className="text-xs text-slate-500 font-mono-data flex items-center gap-2">
                        <span>{d.phone || "no phone"}</span>
                        {trips ? <span className="text-ps-blue font-bold tracking-normal">· {trips} trips this month</span> : null}
                      </div>
                    </div>
                  </div>
                  <button onClick={() => del(d.id)} data-testid={`delete-driver-${d.id}`} className="text-slate-400 hover:text-red-600 shrink-0 ml-2"><Trash size={14} /></button>
                </div>
              );
            })}
            {rows.length === 0 && <div className="bg-white p-8 text-center text-slate-500 col-span-full">No drivers yet.</div>}
          </div>
        </div>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-slate-900/50 grid place-items-center" onClick={() => setOpen(false)} data-testid="driver-dialog">
          <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="w-full max-w-md bg-white border border-slate-200">
            <div className="p-6 border-b border-slate-200 flex items-center justify-between">
              <h2 className="font-heading font-black text-xl tracking-tight">Register driver</h2>
              <button type="button" onClick={() => setOpen(false)} className="text-slate-500"><X size={20} /></button>
            </div>
            <div className="p-6 space-y-4">
              <Field label="Name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} testid="driver-name" required />
              <Field label="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} testid="driver-phone" />
              <Field label="Notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} testid="driver-notes" />
              {err ? <div className="border border-red-300 bg-red-50 text-red-700 text-sm px-3 py-2">{err}</div> : null}
            </div>
            <div className="p-6 border-t border-slate-200 flex gap-3">
              <button type="button" onClick={() => setOpen(false)} className="flex-1 border border-slate-300 py-2.5 text-sm hover:bg-slate-100">Cancel</button>
              <button type="submit" data-testid="driver-submit" className="flex-1 bg-ps-blue text-white py-2.5 rounded-md text-sm font-semibold hover:bg-ps-deep transition-colors">Save</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

function Field({ label, testid, ...props }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-1">{label}</label>
      <input {...props} data-testid={testid} className="w-full border border-slate-300 px-3 py-2 text-sm focus:border-slate-900 font-mono-data" />
    </div>
  );
}
